/**
 * Organization Invite Selector Component
 *
 * Allows users in multiple organizations to select which org to invite partners to.
 */

import React, { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { organizationAPI } from '../services/api';
import { Select } from './ui';
import { showError } from '../utils/toast';
import { unwrapResponse } from '../utils/unwrapResponse';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../hooks/queries/queryKeys';

interface Organization {
  id: string;
  name: string;
  hasPartner: boolean;
  pendingInvites: number;
}

interface OrganizationInviteSelectorProps {
  selectedOrgId: string | null;
  onChange: (orgId: string) => void;
}

export const OrganizationInviteSelector: React.FC<OrganizationInviteSelectorProps> = ({
  selectedOrgId,
  onChange,
}) => {
  const { user } = useAuth();
  const userId = String(user?.id ?? '');

  // GET /organizations/with-invite-status -- confirmed via
  // organizationController.ts's listOrganizationsWithInviteStatus: keyed
  // entirely off the caller's own id (getOrganizationsWithInviteStatus(userId)),
  // no org-scoping param. Scoped by userId per
  // queryKeys.organizations.withInviteStatus's own doc comment. retry: false
  // -- pre-migration was a single fetch-then-catch with no retry loop.
  const orgsQuery = useQuery<Organization[]>({
    queryKey: queryKeys.organizations.withInviteStatus(userId),
    queryFn: async () => {
      const response = await organizationAPI.listWithInviteStatus();
      return unwrapResponse<any>(response.data).organizations;
    },
    enabled: !!user,
    retry: false,
  });

  const organizations = orgsQuery.data ?? [];
  const loading = orgsQuery.isLoading;

  useEffect(() => {
    if (orgsQuery.isError) {
      showError('Failed to load organizations. Please try again.');
    }
  }, [orgsQuery.isError]);

  // Auto-select the first org once loaded, mirroring the pre-migration
  // fetch effect's inline auto-select (only when nothing is selected yet).
  useEffect(() => {
    if (!selectedOrgId && organizations.length > 0) {
      onChange(organizations[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizations, selectedOrgId]);

  if (loading || organizations.length <= 1) return null;

  return (
    <div className="mb-4 p-4 bg-bg-tertiary border border-border-light rounded-lg">
      <Select
        id="org-select"
        value={selectedOrgId || ''}
        onChange={(e) => onChange(e.target.value)}
        size="md"
        label="Invite to organization:"
      >
        {organizations.map((org: Organization) => (
          <option key={org.id} value={org.id}>
            {org.name}
            {!org.hasPartner && ' (no partner)'}
            {org.pendingInvites > 0 && ` (${org.pendingInvites} pending)`}
          </option>
        ))}
      </Select>
    </div>
  );
};
